import { Permission } from '@/domain/permissions';
import NotAllowedPage from '@/pages/403';
import BarChartOutlined from '@ant-design/icons/BarChartOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CodeSandboxOutlined from '@ant-design/icons/CodeSandboxOutlined';
import ContactsOutlined from '@ant-design/icons/ContactsOutlined';
import ControlOutlined from '@ant-design/icons/ControlOutlined';
import DollarOutlined from '@ant-design/icons/DollarOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import MenuOutlined from '@ant-design/icons/MenuOutlined';
import PhoneOutlined from '@ant-design/icons/PhoneOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import TagOutlined from '@ant-design/icons/TagOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import ToolOutlined from '@ant-design/icons/ToolOutlined';
import UserAddOutlined from '@ant-design/icons/UserAddOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import type { FC } from 'react';
import { lazy } from 'react';

const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const AttorneysPage = lazy(() => import('@/pages/contacts/attorneys/AttorneysPage'));
const SecurityLayout = lazy(() => import('@/layouts/SecurityLayout'));
const ContextProvidersLayout = lazy(() => import('@/layouts/ContextProvidersLayout'));
const DashboardPage = lazy(() => import('@/pages/dashboard/[name]/DashboardPage'));
const ContactPage = lazy(() => import('@/pages/contact/ContactPage'));
const CommunicationsPhoneCallsPage = lazy(
  () => import('@/pages/communications/phone-calls/CommunicationsPhoneCallsPage'),
);
const CommunicationsMessagesPage = lazy(
  () => import('@/pages/communications/messages/CommunicationsMessagesPage'),
);
const CommunicationsEmailsPage = lazy(
  () => import('@/pages/communications/emails/CommunicationsEmailsPage'),
);
const TicketsPage = lazy(() => import('@/pages/tickets/[groupId]/TicketsPage'));
const MyTasksPage = lazy(() => import('@/pages/tasks/mine/MyTasksPage'));
const MyProcessTasksPage = lazy(() => import('@/pages/tasks/my-process/MyProcessTasksPage'));
const OnboardingTasksPage = lazy(() => import('@/pages/tasks/onboarding/OnboardingTasksPage'));
const RenovationTasksPage = lazy(() => import('@/pages/tasks/renovation/RenovationTasksPage'));
const ListingTasksPage = lazy(() => import('@/pages/tasks/listing/ListingTasksPage'));
const LeasingTasksPage = lazy(() => import('@/pages/tasks/leasing/LeasingTasksPage'));
const MoveInTasksPage = lazy(() => import('@/pages/tasks/move-in/MoveInTasksPage'));
const MoveOutTasksPage = lazy(() => import('@/pages/tasks/move-out/MoveOutTasksPage'));
const TurnTasksPage = lazy(() => import('@/pages/tasks/turn/TurnTasksPage'));
const DispositionTasksPage = lazy(() => import('@/pages/tasks/dispositions/DispositionTasksPage'));
const EvictionsTasksPage = lazy(() => import('@/pages/tasks/evictions/EvictionsTasksPage'));
const AcquisitionsPropertiesPage = lazy(
  () => import('@/pages/acquisitions/properties/AcquisitionPropertiesPage'),
);
const UnderwritingProfilesPage = lazy(
  () => import('@/pages/acquisitions/profiles/UnderwritingProfilesPage'),
);
const UnderwritingProfileDetailsPage = lazy(
  () => import('@/pages/acquisitions/profiles/[id]/UnderwritingProfileDetailsPage'),
);
const AcquisitionPortfoliosPage = lazy(
  () => import('@/pages/acquisitions/portfolios/AcquisitionPortfoliosPage'),
);
const AcquisitionsPropertyDetailsPage = lazy(
  () => import('@/pages/acquisitions/properties/[id]/AcquisitionsPropertyDetailsPage'),
);
const AcquisitionsPortfolioDetailsPage = lazy(
  () => import('@/pages/acquisitions/portfolios/[id]/AcquisitionsPropertyDetailsPage'),
);
const AcquisitionsRegionalAssumptionsPage = lazy(
  () => import('@/pages/acquisitions/regional-assumptions/AcquisitionsRegionalAssumptionsPage'),
);
const AcquisitionsStateDetailsPage = lazy(
  () => import('@/pages/acquisitions/regional-assumptions/[state]/AcquisitionsStateDetailsPage'),
);
const PropertiesPage = lazy(() => import('@/pages/properties/view/PropertiesPage'));
const UnitRentalPricingPage = lazy(
  () => import('@/pages/prospects/unit-rental-pricing/UnitRentalPricingPage'),
);
const UnitsPage = lazy(() => import('@/pages/properties/units/UnitsPage'));
const FloorPlansPage = lazy(() => import('@/pages/properties/floor-plans/FloorPlansPage'));
const FloorPlanDetailsPage = lazy(
  () => import('@/pages/properties/floor-plans/[id]/FloorPlanDetailsPage'),
);
const UnitsDetailsPage = lazy(() => import('@/pages/units/[id]/UnitDetailsPage'));
const ListingsPage = lazy(() => import('@/pages/properties/listings/ListingsPage'));
const ListingsDetailsPage = lazy(
  () => import('@/pages/properties/listings/[id]/ListingDetailsPage'),
);
const OnboardingPage = lazy(() => import('@/pages/properties/onboardings/OnboardingsPage'));
const OnboardingDetailsPage = lazy(
  () => import('@/pages/properties/onboardings/[id]/OnboardingDetailsPage'),
);
const PropertyUtilitiesPage = lazy(
  () => import('@/pages/properties/utilities/PropertyUtilitiesPage'),
);
const CommunitiesPage = lazy(() => import('@/pages/properties/communities/CommunitiesPage'));
const CommunityDetailsPage = lazy(
  () => import('@/pages/properties/communities/[id]/CommunityDetailsPage'),
);
const PropertyDetailsPage = lazy(() => import('@/pages/properties/[id]/PropertyDetailsPage'));
const SystemsPage = lazy(() => import('@/pages/systems/view/SystemsPage'));
const SystemDetailsPage = lazy(() => import('@/pages/systems/[id]/SystemDetailsPage'));
const SystemsUnassignedPropertiesPage = lazy(
  () => import('@/pages/systems/system-installation/SystemsUnassignedPropertiesPage'),
);
const SystemsVacantPropertiesPage = lazy(
  () => import('@/pages/systems/vacant-properties/SystemsVacantPropertiesPage'),
);
const SystemsOccupiedPropertiesPage = lazy(
  () => import('@/pages/systems/occupied-properties/SystemsOccupiedPropertiesPage'),
);
const PropertyGroups = lazy(() => import('@/pages/properties/property-groups/PropertyGroupsPage'));

const LeadsPage = lazy(() => import('@/pages/prospects/leads/LeadsPage'));
const ShowingsPage = lazy(() => import('@/pages/prospects/showings/ShowingsPage'));
const MyShowingsPage = lazy(() => import('@/pages/prospects/my-showings/MyShowingsPage'));
const LeasingPage = lazy(() => import('@/pages/prospects/leasing/LeasingsPage'));
const LeasingDetailsPage = lazy(() => import('@/pages/prospects/leasing/[id]/LeasingDetailsPage'));
const UnitApplicationsPage = lazy(
  () => import('@/pages/prospects/unit-applications/UnitApplicationsPage'),
);
const UnitApplicationDetailsPage = lazy(
  () => import('@/pages/prospects/unit-applications/[id]/UnitApplicationDetailsPage'),
);
const ApplicationsPage = lazy(() => import('@/pages/prospects/applications/ApplicationsPage'));
const RenewalsPage = lazy(() => import('@/pages/residents/renewals/RenewalsPage'));
const RenewalsDetailsPage = lazy(
  () => import('@/pages/residents/renewals/[id]/RenewalsDetailsPage'),
);
const TurnsPage = lazy(() => import('@/pages/maintenance/turns/all/TurnsPage'));
const TurnWorkOrdersPage = lazy(
  () => import('@/pages/maintenance/turns/turn-work-orders/TurnWorkOrdersPage'),
);
const TurnJobsPage = lazy(() => import('@/pages/maintenance/turns/turn-jobs/TurnJobsPage'));
const RehabPage = lazy(() => import('@/pages/maintenance/rehabs/all/RehabsPage'));
const RehabWorkOrdersPage = lazy(
  () => import('@/pages/maintenance/rehabs/rehab-work-orders/RehabWorkOrders'),
);
const RehabJobsPage = lazy(() => import('@/pages/maintenance/rehabs/rehab-jobs/RehabJobs'));
const TurnsDetailsPage = lazy(() => import('@/pages/maintenance/turn-rehabs/[id]/TurnDetailPage'));
const JobsPage = lazy(() => import('@/pages/maintenance/jobs/all/JobsPage'));
const JobWorkOrdersPage = lazy(
  () => import('@/pages/maintenance/jobs/job-work-orders/JobsWorkOrdersPage'),
);
const JobsDetailsPage = lazy(() => import('@/pages/maintenance/jobs/[id]/JobDetailsPage'));
const WorkOrdersPage = lazy(() => import('@/pages/maintenance/work-orders/WorkOrdersPage'));
const MyWorkOrdersPage = lazy(() => import('@/pages/maintenance/my-work-orders/MyWorkOrdersPage'));
const WorkOrdersDetailsPage = lazy(
  () => import('@/pages/maintenance/work-orders/[id]/WorkOrderDetailsPage'),
);
const RecurringWorkOrdersPage = lazy(
  () => import('@/pages/maintenance/recurring-work-orders/all/RecurringWorkOrdersPage'),
);
const RecurringServicePropertiesPage = lazy(
  () =>
    import(
      '@/pages/maintenance/recurring-work-orders/recurring-service-properties/RecurringServicePropertiesPage'
    ),
);
const BidsPage = lazy(() => import('@/pages/maintenance/bids/BidsPage'));
const VendorsPage = lazy(() => import('@/pages/maintenance/vendors/VendorsPage'));
const VendorDetailsPage = lazy(() => import('@/pages/maintenance/vendors/[id]/VendorDetailsPage'));
const RecurringServiceProvidersPage = lazy(
  () =>
    import(
      '@/pages/maintenance/recurring-work-orders/recurring-service-providers/RecurringServiceProvidersPage'
    ),
);
const RecurringWorkOrderTemplatesPage = lazy(
  () =>
    import('@/pages/maintenance/recurring-work-orders/templates/RecurringWorkOrderTemplatesPage'),
);
const RealEstateAgentPage = lazy(
  () => import('@/pages/contacts/real-estate-agent/RealStateAgentPage'),
);
const ResidentsPage = lazy(() => import('@/pages/residents/view/ResidentsPage'));
const ResidentsCollectionsPage = lazy(
  () => import('@/pages/residents/collections/ResidentCollectionsPage'),
);
const MoveInsPage = lazy(() => import('@/pages/residents/move-ins/MoveInsPage'));
const MoveInDetailsPage = lazy(() => import('@/pages/residents/move-ins/[id]/MoveInDetailsPage'));
const MoveOutsPage = lazy(() => import('@/pages/residents/move-outs/MoveOutsPage'));
const MoveOutDetailsPage = lazy(
  () => import('@/pages/residents/move-outs/[id]/MoveOutDetailsPage'),
);
const DisputesPage = lazy(() => import('@/pages/residents/disputes/DisputesPage'));
const DisputeDetailsPage = lazy(() => import('@/pages/residents/disputes/[id]/DisputeDetailsPage'));
const EvictionsPage = lazy(() => import('@/pages/residents/evictions/EvictionsPage'));
const EvictionDetailsPage = lazy(
  () => import('@/pages/residents/evictions/[id]/EvictionDetailsPage'),
);
const ReportsPage = lazy(() => import('@/pages/report-pages/ReportsPage'));
const ReportDetailsPage = lazy(() => import('@/pages/report-pages/[id]/ReportDetailsPage'));
const UserDetailsPage = lazy(() => import('@/pages/users/[id]/UserDetailsPage'));
const SurveysPage = lazy(() => import('@/pages/surveys/SurveysPage'));
const SurveyDetailsPage = lazy(() => import('@/pages/surveys/[surveyId]/SurveyDetailsPage'));
const SurveyAnswerDetailsPage = lazy(
  () => import('@/pages/surveys/[surveyId]/answers/[answerId]/SurveyAnswerDetailsPage'),
);
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const PlaidVerificationPage = lazy(
  () => import('@/pages/settings/plaid-verification/PlaidVerificationPage'),
);
const UsersPage = lazy(() => import('@/pages/users/view/UsersPage'));
const EmailAddressFiltersPage = lazy(
  () => import('@/pages/settings/email-address-filters/EmailAddressFiltersPage'),
);
const UserGroupsPage = lazy(() => import('@/pages/settings/user-groups/UserGroupsPage'));
const UserGroupsDetailsPage = lazy(
  () => import('@/pages/settings/user-groups/[id]/UserGroupsDetailsPage'),
);
const PermissionsPage = lazy(() => import('@/pages/settings/permissions/PermissionsPage'));
const CompaniesPage = lazy(() => import('@/pages/settings/companies/CompaniesPage'));
const IlsSyndicationPage = lazy(
  () => import('@/pages/settings/ils-syndication/IlsSyndicationPage'),
);
const PropertyManagersPage = lazy(
  () => import('@/pages/settings/property-managers/PropertyManagersPage'),
);
const PropertyManagerDetailsPage = lazy(
  () => import('@/pages/settings/property-managers/[id]/PropertyManagerDetailsPage'),
);
const OfficesPage = lazy(
  () => import('@/pages/settings/property-managers/[id]/office/[id]/OfficeDetailsPage'),
);
const EmailTemplatesPage = lazy(
  () => import('@/pages/settings/email-templates/EmailTemplatesPage'),
);
const CreateEmailTemplatePage = lazy(
  () => import('@/pages/settings/email-templates/create/CreateEmailTemplatePage'),
);
const EmailDetailsPage = lazy(
  () => import('@/pages/settings/email-templates/[id]/EmailDetailsPage'),
);
const FormsPage = lazy(() => import('@/pages/settings/forms/FormsPage'));
const FormDetailsPage = lazy(() => import('@/pages/settings/forms/[id]/FormDetailsPage'));
const PetPoliciesPage = lazy(() => import('@/pages/settings/pet-policy/PetPoliciesPage'));
const TemperatureProfilesPage = lazy(
  () => import('@/pages/settings/temperature-profiles/TemperatureProfilesPage'),
);
const MetadataPlaygroundPage = lazy(
  () => import('@/pages/metadata-playground/MetadataPlaygroundPage'),
);
const PromotionsPage = lazy(() => import('@/pages/settings/promotions/PromotionsPage'));
const PromotionsDetailsPage = lazy(
  () => import('@/pages/settings/promotions/[id]/PromotionDetailsPage'),
);
const TicketGroupsPage = lazy(() => import('@/pages/settings/ticket-groups/TicketGroupsPage'));
const MaintenanceSettingsPage = lazy(
  () => import('@/pages/settings/maintenance-settings/MaintenanceSettingsPage'),
);
const PastDueSettingsPage = lazy(
  () => import('@/pages/settings/past-due-settings/PastDueSettingsPage'),
);
const EmailGroupsPage = lazy(() => import('@/pages/settings/email-groups/EmailGroupsPage'));
const TicketCreationRulesPage = lazy(
  () => import('@/pages/settings/ticket-creation-rules/TicketCreationRulesPage'),
);
const RegionsPage = lazy(() => import('@/pages/settings/regions/RegionsPage'));
const UserRolesPage = lazy(() => import('@/pages/settings/user-roles/UserRolesPage'));
const BrandsPage = lazy(() => import('@/pages/settings/brands/BrandsPage'));
const InvoiceAccountsPage = lazy(
  () => import('@/pages/settings/invoice-accounts/InvoiceAccountsPage'),
);
const BrandDetailsPage = lazy(() => import('@/pages/settings/brands/[id]/BrandDetailsPage'));
const TaskTemplatesPage = lazy(() => import('@/pages/settings/task-templates/TaskTemplatesPage'));
const PresetTaskPage = lazy(() => import('@/pages/settings/preset-tasks/PresetTaskPage'));
const HoldingCompaniesPage = lazy(
  () => import('@/pages/settings/holding-companies/HoldingCompaniesPage'),
);
const ApplicationFeesPage = lazy(
  () => import('@/pages/settings/application-fees/ApplicationFeesPage'),
);
const UnitChargeTemplatesPage = lazy(
  () => import('@/pages/settings/unit-charge-templates/UnitChargeTemplatesPage'),
);
const UnitChargeTemplatesDetailsPage = lazy(
  () => import('@/pages/settings/unit-charge-templates/[id]/UnitChargeTemplatesDetailsPage'),
);
const PropertyGroupPointsOfContactPage = lazy(
  () =>
    import('@/pages/settings/property-group-points-of-contact/PropertyGroupPointsOfContactPage'),
);
const FundsPage = lazy(() => import('@/pages/settings/funds/FundsPage'));
const ApiClientsPage = lazy(() => import('@/pages/settings/api-clients/ApiClientsPage'));
const UserProfilePage = lazy(() => import('@/pages/me/UserProfilePage'));
const DispositionsPage = lazy(() => import('@/pages/properties/dispositions/DispositionsPage'));
const DispositionOfferDetailsRedirectPage = lazy(
  () => import('@/pages/properties/dispositions/offers/[id]/DispositionOfferDetailsRedirectPage'),
);
const DispositionOfferDetailsPage = lazy(
  () =>
    import(
      '@/pages/properties/dispositions/[dispositionId]/offers/[offerId]/DispositionOfferDetailsPage'
    ),
);
const DispositionDetailsPage = lazy(
  () => import('@/pages/properties/dispositions/[dispositionId]/DispositionDetailsPage'),
);
const PropertyGroupDetailsPage = lazy(
  () => import('@/pages/properties/property-groups/[id]/PropertyGroupDetailsPage'),
);
const AutoFillDictionaryPage = lazy(
  () => import('@/pages/document-templates/autofill-dictionary/AutoFillDictionaryPage'),
);
const DocumentTemplatesPage = lazy(
  () => import('@/pages/document-templates/view/DocumentTemplatesPage'),
);
const InvoicesPage = lazy(() => import('@/pages/invoices/InvoicesPage'));
const InvoiceDetailsPage = lazy(() => import('@/pages/invoices/[id]/InvoiceDetailsPage'));
const WelcomePage = lazy(() => import('@/pages/welcome/WelcomePage'));
const UserRolesDetailsPage = lazy(
  () => import('@/pages/settings/user-roles/[id]/UserRolesDetailsPage'),
);
const CallCenterPage = lazy(() => import('@/pages/call-center/CallCenterPage'));
const PublicAccountAgreementsPage = lazy(
  () => import('@/pages/settings/public-account-agreements/PublicAccountAgreementsPage'),
);
const PublicAccountAgreementDetailsPage = lazy(
  () => import('@/pages/settings/public-account-agreements/[id]/PublicAccountAgreementDetailsPage'),
);

export type PropifyRoute = {
  path: string;
  component?: Function;
  children?: PropifyRoute[];
  redirect?: string;
  permissions?: Permission[];
  // For more props see: https://umijs.org/en-US/docs/max/layout-menu
  props?: {
    name?: string;
    icon?: FC;
    hideInMenu?: boolean;
    path?: string;
    target?: '_blank';
    [key: string]: unknown;
  };
};

export const routes: PropifyRoute[] = [
  {
    path: '/auth',
    children: [
      {
        path: '/auth/callback',
        component: AuthCallbackPage,
      },
    ],
  },
  {
    path: '/',
    component: SecurityLayout,
    children: [
      {
        path: '/',
        component: ContextProvidersLayout,
        children: [
          {
            path: '/',
            redirect: '/welcome',
          },
          {
            path: '/metadata-playground',
            component: MetadataPlaygroundPage,
            props: {
              name: 'Metadata Playground',
              hideInMenu: true,
            },
          },
          {
            path: '/dashboard',
            permissions: [Permission.READ_DASHBOARD],
            props: {
              name: 'Dashboard',
              icon: BarChartOutlined,
            },
            children: [
              {
                path: '/dashboard',
                redirect: '/dashboard/leasing',
              },
              {
                path: '/dashboard/:name',
                component: DashboardPage,
                props: {
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/call-center',
            component: CallCenterPage,
            permissions: [Permission.READ_CALL_CENTER_DASHBOARD],
            props: {
              name: 'Call Center',
              icon: PhoneOutlined,
            },
          },
          {
            path: '/contacts',
            permissions: [Permission.READ_CONTACT],
            props: {
              name: 'Contacts',
              icon: UserOutlined,
            },
            children: [
              {
                path: '/contacts/attorneys',
                permissions: [Permission.READ_ATTORNEY],
                component: AttorneysPage,
                props: {
                  name: 'Attorneys',
                },
              },
              {
                path: '/contacts/real-estate-agent',
                permissions: [Permission.READ_REAL_ESTATE_AGENT],
                component: RealEstateAgentPage,
                props: {
                  name: 'Real Estate Agents',
                },
              },
            ],
          },
          {
            path: '/contact',
            component: ContactPage,
            props: {
              name: 'Active Claims',
              icon: ContactsOutlined,
            },
          },
          {
            path: '/tickets',
            permissions: [Permission.READ_TICKET_MENU],
            props: {
              name: 'Tickets',
              icon: TagOutlined,
            },
            children: [
              {
                path: '/tickets',
                redirect: '/tickets/all-tickets',
              },
              {
                path: '/tickets/:groupId',
                component: TicketsPage,
                props: {
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/tasks',
            permissions: [Permission.READ_TASK_MENU],
            props: {
              name: 'Tasks',
              icon: MenuOutlined,
            },
            children: [
              {
                path: '/tasks/dispositions',
                permissions: [Permission.READ_DISPOSITIONS_TASKS],
                component: DispositionTasksPage,
                props: {
                  name: 'Disposition Tasks',
                },
              },
              {
                path: '/tasks/evictions',
                permissions: [Permission.READ_EVICTIONS_TASKS],
                component: EvictionsTasksPage,
                props: {
                  name: 'Evictions Tasks',
                },
              },
              {
                path: '/tasks/leasing',
                permissions: [Permission.READ_LEASING_TASKS],
                component: LeasingTasksPage,
                props: {
                  name: 'Leasing Tasks',
                },
              },
              {
                path: '/tasks/listing',
                permissions: [Permission.READ_LISTING_TASKS],
                component: ListingTasksPage,
                props: {
                  name: 'Listing Tasks',
                },
              },
              {
                path: '/tasks/mine',
                permissions: [Permission.READ_MY_TASKS],
                component: MyTasksPage,
                props: {
                  name: 'My Tasks',
                },
              },
              {
                path: '/tasks/move-in',
                permissions: [Permission.READ_MOVE_IN_TASKS],
                component: MoveInTasksPage,
                props: {
                  name: 'Move-In Tasks',
                },
              },
              {
                path: '/tasks/move-out',
                permissions: [Permission.READ_MOVE_OUT_TASKS],
                component: MoveOutTasksPage,
                props: {
                  name: 'Move-Out Tasks',
                },
              },
              {
                path: '/tasks/my-process',
                permissions: [Permission.READ_MY_PROCESS_TASKS],
                component: MyProcessTasksPage,
                props: {
                  name: 'My Process Tasks',
                },
              },
              {
                path: '/tasks/onboarding',
                permissions: [Permission.READ_ONBOARDING_TASKS],
                component: OnboardingTasksPage,
                props: {
                  name: 'Onboarding Tasks',
                },
              },
              {
                path: '/tasks/renovation',
                permissions: [Permission.READ_RENOVATION_TASKS],
                component: RenovationTasksPage,
                props: {
                  name: 'Renovation Tasks',
                },
              },
              {
                path: '/tasks/turn',
                permissions: [Permission.READ_TURN_TASKS],
                component: TurnTasksPage,
                props: {
                  name: 'Turn Tasks',
                },
              },
            ],
          },
          {
            path: '/prospects',
            permissions: [Permission.READ_PROSPECT_MENU],
            props: {
              name: 'Leasing',
              icon: UserAddOutlined,
            },
            children: [
              {
                path: '/prospects',
                redirect: '/prospects/leasing',
                permissions: [Permission.READ_LEASING],
              },
              {
                path: '/prospects/leasing',
                component: LeasingPage,
                permissions: [Permission.READ_LEASING],
                props: {
                  name: 'Leasing Process',
                },
              },
              {
                path: '/prospects/leasing/:id',
                component: LeasingDetailsPage,
                permissions: [Permission.READ_LEASING],
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/prospects/unit-rental-pricing',
                component: UnitRentalPricingPage,
                permissions: [Permission.READ_UNIT_RENTAL_PRICING],
                props: {
                  name: 'Unit Rental Pricing',
                },
              },
              {
                path: '/prospects/leads',
                component: LeadsPage,
                permissions: [Permission.READ_LEAD],
                props: {
                  name: 'Leads',
                },
              },
              {
                path: '/prospects/showings',
                permissions: [Permission.READ_SHOWING],
                props: {
                  name: 'Showings',
                },
                component: ShowingsPage,
              },
              {
                path: '/prospects/my-showings',
                permissions: [Permission.READ_MY_SHOWINGS],
                props: {
                  name: 'My Showings',
                },
                component: MyShowingsPage,
              },
              {
                path: '/prospects/unit-applications',
                component: UnitApplicationsPage,
                permissions: [Permission.READ_APPLICATION],
                props: {
                  name: 'Unit Applications',
                },
              },
              {
                path: '/prospects/unit-applications/:id',
                component: UnitApplicationDetailsPage,
                permissions: [Permission.READ_APPLICATION],
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/prospects/applications',
                component: ApplicationsPage,
                permissions: [Permission.READ_PROSPECT_APPLICATION],
                props: {
                  name: 'Prospect Applications',
                },
              },
            ],
          },
          {
            path: '/document-templates',
            props: {
              name: 'Document Templates',
              icon: FileTextOutlined,
              // This is going to be available when document templates feature is complete
              hideInMenu: true,
            },
            children: [
              {
                path: '/document-templates/view',
                component: DocumentTemplatesPage,
                props: {
                  name: 'Document Templates',
                },
              },
              {
                path: '/document-templates/autofill-dictionary',
                component: AutoFillDictionaryPage,
                props: {
                  name: 'Auto-Fill Dictionary',
                },
              },
            ],
          },
          {
            path: '/maintenance',
            permissions: [Permission.READ_FIELD_OPERATION_MENU],
            props: {
              name: 'Field Operations',
              icon: ToolOutlined,
            },
            children: [
              {
                path: '/maintenance',
                redirect: '/maintenance/work-orders',
              },
              {
                path: '/maintenance/my-work-orders',
                permissions: [Permission.READ_MY_WORK_ORDERS],
                component: MyWorkOrdersPage,
                props: {
                  name: 'My Work Orders',
                },
              },
              {
                path: '/maintenance/work-orders',
                component: WorkOrdersPage,
                permissions: [Permission.READ_GENERAL_WORK_ORDER],
                props: {
                  name: 'Work Orders',
                  showWorkOrdersCount: false, // I'm skipping this in case we need to use them again
                },
              },
              {
                path: '/maintenance/bids',
                component: BidsPage,
                permissions: [Permission.READ_BID],
                props: {
                  name: 'Bids',
                },
              },
              {
                path: '/maintenance/vendors',
                component: VendorsPage,
                permissions: [Permission.READ_VENDOR_MENU],
                props: {
                  name: 'Vendors',
                },
              },
              {
                path: '/maintenance/turns',
                permissions: [Permission.READ_TURN_MENU],
                props: {
                  name: 'Turns',
                  showTurnRehabsCounts: false, // I'm skipping this in case we need to use them again
                },
                children: [
                  {
                    path: '/maintenance/turns',
                    redirect: '/maintenance/turns/all',
                  },
                  {
                    path: '/maintenance/turns/all',
                    component: TurnsPage,
                    props: {
                      name: 'Turn Process',
                    },
                  },
                  {
                    path: '/maintenance/turns/turn-work-orders',
                    permissions: [Permission.READ_TURN_WORK_ORDERS],
                    component: TurnWorkOrdersPage,
                    props: {
                      name: 'Turn Work Orders',
                    },
                  },
                  {
                    path: '/maintenance/turns/turn-jobs',
                    permissions: [Permission.READ_TURN_JOBS],
                    component: TurnJobsPage,
                    props: {
                      name: 'Turn Jobs',
                    },
                  },
                ],
              },
              {
                path: '/maintenance/rehabs',
                permissions: [Permission.READ_REHAB_MENU],
                props: {
                  name: 'Renovations',
                  showTurnRehabsCounts: false, // I'm skipping this in case we need to use them again
                },
                children: [
                  {
                    path: '/maintenance/rehabs',
                    redirect: '/maintenance/rehabs/all',
                  },
                  {
                    path: '/maintenance/rehabs/all',
                    component: RehabPage,
                    props: {
                      name: 'Renovations Process',
                    },
                  },
                  {
                    path: '/maintenance/rehabs/rehab-work-orders',
                    permissions: [Permission.READ_REHAB_WORK_ORDER],
                    component: RehabWorkOrdersPage,
                    props: {
                      name: 'Renovation Work Orders',
                    },
                  },
                  {
                    path: '/maintenance/rehabs/rehab-jobs',
                    permissions: [Permission.READ_REHAB_JOBS],
                    component: RehabJobsPage,
                    props: {
                      name: 'Renovation Jobs',
                    },
                  },
                ],
              },
              {
                path: '/maintenance/turn-rehabs/:id',
                component: TurnsDetailsPage,
                props: {
                  name: 'Turns/Rehabs',
                  hideInMenu: true,
                },
              },
              {
                path: '/maintenance/jobs',
                permissions: [Permission.READ_MAINTENANCE_JOB_MENU],
                props: {
                  name: 'Maint. Jobs',
                },
                children: [
                  {
                    path: '/maintenance/jobs',
                    redirect: '/maintenance/jobs/all',
                  },
                  {
                    path: '/maintenance/jobs/all',
                    component: JobsPage,
                    props: {
                      // cspell:ignore Maint
                      name: 'Maint. Jobs',
                      showJobsCounts: false, // I'm skipping this in case we need to use them again
                    },
                  },
                  {
                    path: '/maintenance/jobs/job-work-orders',
                    permissions: [Permission.READ_MAINTENANCE_JOB_WORK_ORDERS],
                    component: JobWorkOrdersPage,
                    props: {
                      // cspell:ignore Maint
                      name: 'Maint. Job Work Orders',
                      showJobsCounts: false,
                    },
                  },
                ],
              },
              {
                path: '/maintenance/recurring-work-orders',
                permissions: [Permission.READ_RECURRING_FIELD_OPS_MENU],
                props: {
                  name: 'Recurring',
                },
                children: [
                  {
                    path: '/maintenance/recurring-work-orders',
                    redirect: '/maintenance/recurring-work-orders/all',
                  },
                  {
                    path: '/maintenance/recurring-work-orders/all',
                    component: RecurringWorkOrdersPage,
                    props: {
                      name: 'Recurring Work Orders',
                    },
                  },
                  {
                    path: '/maintenance/recurring-work-orders/recurring-service-properties',
                    permissions: [Permission.READ_RECURRING_WORK_ORDERS_PROPERTIES],
                    component: RecurringServicePropertiesPage,
                    props: {
                      name: 'Recurring WO Properties',
                    },
                  },
                  {
                    path: '/maintenance/recurring-work-orders/recurring-service-providers',
                    permissions: [Permission.READ_RECURRING_SERVICE_PROVIDERS],
                    component: RecurringServiceProvidersPage,
                    props: {
                      name: 'Recurring WO Providers',
                    },
                  },
                  {
                    path: '/maintenance/recurring-work-orders/templates',
                    permissions: [Permission.READ_RECURRING_WORK_ORDER_TEMPLATES],
                    component: RecurringWorkOrderTemplatesPage,
                    props: {
                      name: 'Recurring WO Templates',
                    },
                  },
                ],
              },
              {
                path: '/maintenance/jobs/:id',
                component: JobsDetailsPage,
                permissions: [Permission.READ_JOB],
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/maintenance/work-orders/:id',
                component: WorkOrdersDetailsPage,
                permissions: [Permission.READ_WORK_ORDER],
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/maintenance/vendors/:id',
                component: VendorDetailsPage,
                permissions: [Permission.READ_VENDOR_MENU],
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/residents',
            permissions: [Permission.READ_RESIDENT_MENU],
            props: {
              name: 'Residents',
              icon: TeamOutlined,
            },
            children: [
              {
                path: '/residents',
                redirect: '/residents/view',
              },
              {
                path: '/residents/view',
                component: ResidentsPage,
                permissions: [Permission.READ_RESIDENT],
                props: {
                  name: 'Residents',
                },
              },
              {
                path: '/residents/move-ins',
                component: MoveInsPage,
                permissions: [Permission.READ_MOVE_IN],
                props: {
                  name: 'Move-Ins',
                },
              },
              {
                path: '/residents/move-ins/:id',
                component: MoveInDetailsPage,
                permissions: [Permission.READ_MOVE_IN],
              },
              {
                path: '/residents/renewals',
                component: RenewalsPage,
                permissions: [Permission.READ_RENEWAL],
                props: {
                  name: 'Renewals',
                },
              },
              {
                path: '/residents/renewals/:id',
                component: RenewalsDetailsPage,
                permissions: [Permission.READ_RENEWAL],
                props: {
                  name: 'Renewal',
                  hideInMenu: true,
                },
              },
              {
                path: '/residents/move-outs',
                component: MoveOutsPage,
                permissions: [Permission.READ_MOVE_OUT],
                props: {
                  name: 'Move-Outs',
                },
              },
              {
                path: '/residents/move-outs/:id',
                component: MoveOutDetailsPage,
                permissions: [Permission.READ_MOVE_OUT],
              },
              {
                path: '/residents/collections',
                component: ResidentsCollectionsPage,
                permissions: [Permission.READ_COLLECTION],
                props: {
                  name: 'Past Due',
                },
              },
              {
                path: '/residents/evictions',
                component: EvictionsPage,
                // permissions: [Permission.READ_EVICTIONS],
                props: {
                  name: 'Evictions',
                },
              },
              {
                path: '/residents/evictions/:id',
                component: EvictionDetailsPage,
                // permissions: [Permission.READ_EVICTIONS],
              },
              {
                path: '/residents/disputes',
                component: DisputesPage,
                props: {
                  name: 'Disputes',
                  hideInMenu: true,
                },
              },
              {
                path: '/residents/disputes/:id',
                component: DisputeDetailsPage,
                props: {
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/acquisitions',
            props: {
              name: 'Acquisitions',
              icon: CodeSandboxOutlined,
              hideInMenu: true,
            },
            children: [
              {
                path: '/acquisitions',
                redirect: '/acquisitions/properties',
              },
              {
                path: '/acquisitions/properties',
                component: AcquisitionsPropertiesPage,
                props: {
                  name: 'Properties',
                },
              },
              {
                path: '/acquisitions/portfolios',
                component: AcquisitionPortfoliosPage,
                props: {
                  name: 'Portfolios',
                },
              },
              {
                path: '/acquisitions/profiles',
                component: UnderwritingProfilesPage,
                props: {
                  name: 'Underwriting Profiles',
                },
              },
              {
                path: '/acquisitions/properties/:id',
                component: AcquisitionsPropertyDetailsPage,
                props: {
                  name: 'Property',
                  hideInMenu: true,
                },
              },
              {
                path: '/acquisitions/portfolios/:id',
                component: AcquisitionsPortfolioDetailsPage,
                props: {
                  name: 'Portfolio Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/acquisitions/portfolios/:id/properties/:propertyId',
                component: AcquisitionsPropertyDetailsPage,
                props: {
                  name: 'Portfolio Property',
                  hideInMenu: true,
                },
              },
              {
                path: '/acquisitions/profiles/:id',
                component: UnderwritingProfileDetailsPage,
                props: {
                  name: 'Profile',
                  hideInMenu: true,
                },
              },
              {
                path: '/acquisitions/regional-assumptions',
                component: AcquisitionsRegionalAssumptionsPage,
                props: {
                  name: 'Regional Assumptions',
                },
              },
              {
                path: '/acquisitions/regional-assumptions/:state',
                component: AcquisitionsStateDetailsPage,
                props: {
                  name: 'State Details',
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/properties',
            permissions: [Permission.READ_PROPERTY_MENU],
            props: {
              name: 'Properties',
              icon: HomeOutlined,
            },
            children: [
              {
                path: '/properties',
                redirect: '/properties/view',
                permissions: [Permission.READ_PROPERTY],
              },
              {
                path: '/properties/view',
                component: PropertiesPage,
                permissions: [Permission.READ_PROPERTY],
                props: {
                  name: 'Properties',
                },
              },
              {
                path: '/properties/property-groups',
                component: PropertyGroups,
                permissions: [Permission.READ_PROPERTY_GROUP],
                props: {
                  name: 'Property Groups',
                },
              },
              {
                path: '/properties/property-groups/:id',
                component: PropertyGroupDetailsPage,
                permissions: [Permission.READ_PROPERTY_GROUP],
                props: {
                  name: 'Property Group Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/units',
                permissions: [Permission.READ_UNITS_PAGE],
                component: UnitsPage,
                props: {
                  name: 'Units',
                },
              },
              {
                path: '/properties/floor-plans',
                permissions: [Permission.READ_FLOOR_PLANS_PAGE],
                component: FloorPlansPage,
                props: {
                  name: 'Floor Plans',
                },
              },
              {
                path: '/properties/floor-plans/:id',
                component: FloorPlanDetailsPage,
                props: {
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/listings',
                component: ListingsPage,
                permissions: [Permission.READ_LISTING_PROCESS],
                props: {
                  name: 'Listings',
                },
              },
              {
                path: '/properties/listings/:id',
                component: ListingsDetailsPage,
                permissions: [Permission.READ_LISTING_PROCESS],
                props: {
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/onboardings',
                component: OnboardingPage,
                permissions: [Permission.READ_ONBOARDING_PROCESS],
                props: {
                  name: 'Onboardings',
                },
              },
              {
                path: '/properties/onboardings/:id',
                component: OnboardingDetailsPage,
                permissions: [Permission.READ_ONBOARDING_PROCESS],
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/dispositions',
                component: DispositionsPage,
                permissions: [Permission.READ_DISPOSITION],
                props: {
                  name: 'Dispositions',
                },
              },
              {
                path: '/properties/dispositions/offers/:id',
                component: DispositionOfferDetailsRedirectPage,
                permissions: [Permission.READ_DISPOSITION],
                props: {
                  name: 'Offer Redirect',
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/dispositions/:dispositionId/offers/:id',
                component: DispositionOfferDetailsPage,
                permissions: [Permission.READ_DISPOSITION],
                props: {
                  name: 'Offer',
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/dispositions/:id',
                component: DispositionDetailsPage,
                permissions: [Permission.READ_DISPOSITION],
                props: {
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/utilities',
                component: PropertyUtilitiesPage,
                permissions: [Permission.READ_UTILITY],
                props: {
                  name: 'Utilities',
                },
              },
              {
                path: '/properties/communities',
                component: CommunitiesPage,
                permissions: [Permission.READ_COMMUNITY_MENU, Permission.READ_COMMUNITIES_PAGE],
                props: {
                  name: 'Communities',
                },
              },
              {
                path: '/properties/communities/:id',
                component: CommunityDetailsPage,
                props: {
                  name: 'Community',
                  hideInMenu: true,
                },
              },
              {
                path: '/properties/:id',
                component: PropertyDetailsPage,
                permissions: [Permission.READ_PROPERTY],
                props: {
                  name: 'Manage',
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/systems',
            permissions: [Permission.READ_SMART_HOME_MENU],
            props: {
              name: 'Smart Home',
              icon: ControlOutlined,
            },
            children: [
              {
                path: '/systems',
                redirect: '/systems/view',
                permissions: [Permission.READ_SYSTEM],
              },
              {
                path: '/systems/view',
                component: SystemsPage,
                permissions: [Permission.READ_SYSTEM],
                props: {
                  name: 'Systems',
                },
              },
              {
                path: '/systems/:id',
                component: SystemDetailsPage,
                permissions: [Permission.READ_SYSTEM],
                props: {
                  name: 'System Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/systems/system-installation',
                component: SystemsUnassignedPropertiesPage,
                permissions: [Permission.READ_SYSTEM_INSTALLATION],
                props: {
                  name: 'System Installation',
                },
              },
              {
                path: '/systems/vacant-properties',
                component: SystemsVacantPropertiesPage,
                permissions: [Permission.READ_SYSTEM_VACANT_UNIT],
                props: {
                  name: 'Vacant Properties',
                },
              },
              {
                path: '/systems/occupied-properties',
                component: SystemsOccupiedPropertiesPage,
                permissions: [Permission.READ_SYSTEM_OCCUPIED_UNIT],
                props: {
                  name: 'Occupied Properties',
                },
              },
            ],
          },
          {
            path: '/invoices',
            component: InvoicesPage,
            props: {
              name: 'Invoice Approvals',
              icon: DollarOutlined,
            },
            permissions: [Permission.READ_INVOICE],
          },
          {
            path: '/invoices/:id',
            component: InvoiceDetailsPage,
            props: {
              hideInMenu: true,
            },
            permissions: [Permission.READ_INVOICE],
          },
          {
            path: '/units/:id',
            component: UnitsDetailsPage,
            props: {
              name: 'Unit',
              hideInMenu: true,
            },
          },
          {
            path: '/users',
            props: {
              name: 'Users',
              icon: TeamOutlined,
              hideInMenu: true,
            },
            children: [
              {
                path: '/users/view',
                component: UsersPage,
                props: {
                  name: 'View',
                },
              },
              {
                path: '/users/:id',
                component: UserDetailsPage,
                permissions: [Permission.UPDATE_USER],
                props: {
                  name: 'Manage',
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/communications',
            permissions: [Permission.READ_COMMUNICATION_MENU],
            props: {
              name: 'Communications',
              icon: PhoneOutlined,
            },
            children: [
              {
                path: '/communications',
                redirect: '/communications/messages',
              },
              {
                path: '/communications/phone-calls',
                component: CommunicationsPhoneCallsPage,
                props: {
                  name: 'Calls',
                },
              },
              {
                path: '/communications/messages',
                component: CommunicationsMessagesPage,
                props: {
                  name: 'Messages',
                },
              },
              {
                path: '/communications/emails',
                component: CommunicationsEmailsPage,
                props: {
                  name: 'Emails',
                },
              },
            ],
          },
          {
            path: '/report-pages',
            permissions: [Permission.READ_REPORTS_PAGE],
            props: {
              name: 'Reports',
              icon: CheckCircleOutlined,
            },
            children: [
              {
                path: '/report-pages',
                component: ReportsPage,
                props: {
                  name: 'Reports',
                  hideInMenu: true,
                },
              },
              {
                path: '/report-pages/:id',
                component: ReportDetailsPage,
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/surveys',
            permissions: [Permission.READ_SURVEY],
            props: {
              name: 'Surveys',
              icon: CheckCircleOutlined,
            },
            children: [
              {
                path: '/surveys',
                component: SurveysPage,
                props: {
                  name: 'Surveys',
                  hideInMenu: true,
                },
              },
              {
                path: '/surveys/:id',
                component: SurveyDetailsPage,
                props: {
                  name: 'Survey',
                  hideInMenu: true,
                },
              },
              {
                path: '/surveys/:surveyId/answers/:answerId',
                component: SurveyAnswerDetailsPage,
                props: {
                  name: 'Survey Response',
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/support',
            props: {
              name: 'Knowledge Center',
              icon: QuestionCircleOutlined,
              path: 'https://propifyfaq.freshdesk.com/support/solutions',
              target: '_blank',
            },
            children: [
              {
                path: '/support',
                redirect: '/',
              },
            ],
          },
          {
            path: '/settings',
            permissions: [Permission.READ_SETTING_MENU],
            props: {
              name: 'Settings',
              icon: SettingOutlined,
            },
            children: [
              {
                path: '/settings',
                component: SettingsPage,
                props: {
                  name: 'Settings',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/email-address-filters',
                component: EmailAddressFiltersPage,
                props: {
                  name: 'Allowed/Blocked Email',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/users',
                component: UsersPage,
                props: {
                  name: 'Users',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/user-roles',
                component: UserRolesPage,
                props: {
                  name: 'User Roles',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/user-groups',
                component: UserGroupsPage,
                props: {
                  name: 'User Groups',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/user-groups/:id',
                component: UserGroupsDetailsPage,
                permissions: [Permission.READ_USER],
                props: {
                  name: 'User Group Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/permissions',
                component: PermissionsPage,
                props: {
                  name: 'Permissions',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/companies',
                component: CompaniesPage,
                props: {
                  name: 'Companies',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/ils-syndication',
                component: IlsSyndicationPage,
                props: {
                  name: 'ILS Syndication',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/property-managers',
                component: PropertyManagersPage,
                props: {
                  name: 'Property Managers',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/property-managers/:id',
                component: PropertyManagerDetailsPage,
                props: {
                  name: 'Property Manager Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/property-managers/:companyId/office/:id',
                component: OfficesPage,
                props: {
                  name: 'Office Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/email-templates',
                component: EmailTemplatesPage,
                permissions: [Permission.READ_EMAIL_TEMPLATE],
                props: {
                  name: 'Email Templates',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/email-templates/create',
                component: CreateEmailTemplatePage,
                permissions: [Permission.CREATE_EMAIL_TEMPLATE],
                props: {
                  name: 'Create Email Template',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/email-templates/:id',
                component: EmailDetailsPage,
                permissions: [Permission.UPDATE_EMAIL_TEMPLATE],
                props: {
                  name: 'Email Template',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/user-roles/:id',
                component: UserRolesDetailsPage,
                props: {
                  name: 'Role Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/forms',
                component: FormsPage,
                props: {
                  name: 'Forms',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/forms/:id',
                component: FormDetailsPage,
                props: {
                  name: 'Form Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/pet-policy',
                component: PetPoliciesPage,
                props: {
                  name: 'Pet Policy',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/temperature-profiles',
                component: TemperatureProfilesPage,
                props: {
                  name: 'Temperature Profiles',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/promotions',
                component: PromotionsPage,
                props: {
                  name: 'Promotions',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/promotions/:id',
                component: PromotionsDetailsPage,
                props: {
                  name: 'Promotions Manage',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/ticket-groups',
                component: TicketGroupsPage,
                props: {
                  name: 'Ticket Groups',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/maintenance-settings',
                component: MaintenanceSettingsPage,
                props: {
                  name: 'Maintenance Settings',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/past-due-settings',
                component: PastDueSettingsPage,
                props: {
                  name: 'Past Due Settings',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/email-groups',
                component: EmailGroupsPage,
                props: {
                  name: 'Email Groups',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/ticket-creation-rules',
                component: TicketCreationRulesPage,
                props: {
                  name: 'Ticket Creation Rules',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/regions',
                component: RegionsPage,
                props: {
                  name: 'Regions',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/brands',
                component: BrandsPage,
                props: {
                  name: 'Brands',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/invoice-accounts',
                component: InvoiceAccountsPage,
                props: {
                  name: 'Invoice Accounts',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/brands/:id',
                component: BrandDetailsPage,
                props: {
                  name: 'Brand Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/task-templates',
                component: TaskTemplatesPage,
                props: {
                  name: 'Task Templates',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/preset-tasks',
                component: PresetTaskPage,
                props: {
                  name: 'Preset Tasks',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/holding-companies',
                component: HoldingCompaniesPage,
                props: {
                  name: 'Holding Companies',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/application-fees',
                component: ApplicationFeesPage,
                props: {
                  name: 'Application Fees',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/public-account-agreements',
                component: PublicAccountAgreementsPage,
                permissions: [Permission.READ_PUBLIC_ACCOUNT_AGREEMENT],
                props: {
                  name: 'Public Account Agreements',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/public-account-agreements/:id',
                component: PublicAccountAgreementDetailsPage,
                permissions: [Permission.READ_PUBLIC_ACCOUNT_AGREEMENT],
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/unit-charge-templates',
                component: UnitChargeTemplatesPage,
                permissions: [Permission.READ_CHARGE_TEMPLATE],
                props: {
                  name: 'Unit Charge Templates',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/unit-charge-templates/:id',
                component: UnitChargeTemplatesDetailsPage,
                props: {
                  name: 'Details',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/property-group-points-of-contact',
                component: PropertyGroupPointsOfContactPage,
                props: {
                  name: 'Property Group Points of Contact',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/funds',
                component: FundsPage,
                props: {
                  name: 'Funds',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/api-clients',
                component: ApiClientsPage,
                props: {
                  name: 'API Clients',
                  hideInMenu: true,
                },
              },
              {
                path: '/settings/plaid-verification',
                component: PlaidVerificationPage,
                props: {
                  name: 'Plaid Verification',
                  hideInMenu: true,
                },
              },
            ],
          },
          {
            path: '/me',
            component: UserProfilePage,
            props: {
              name: 'Me',
              hideInMenu: true,
            },
          },
          {
            path: '/welcome',
            component: WelcomePage,
            props: {
              name: 'Welcome',
              hideInMenu: true,
            },
          },
        ],
      },
    ],
  },
];

export const unauthorizedRoute: Omit<PropifyRoute, 'path'> = {
  component: NotAllowedPage,
  props: {
    hideInMenu: true,
  },
};
